import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import i18n from '@i18next/i18n';
import { EventEmitter } from '@tencent/workbench';
import { searchImages } from '@src/slide/application/service/ai/core/api/api';
import { SearchImageInfo } from '@src/slide/application/service/ai/core/api/api.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { ISelectionService, SelectedModelType } from '../../selection/selection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import Button from '@tencent/dui/lib/components/Button';
import Input from '@tencent/dui/lib/components/Input';
import Preview from '@tencent/dui/lib/components/Preview';
import styles from '@src/slide/application/service/smart-replace-image/replace-drawer/replace-image-drawer.less';
import { reaction } from 'mobx';
import { ReplaceImageUtils } from '../utils/replace-image-utils';
import searchIcon from '@tencent/docs-design-resources/pc/svg/toolbar_search.svg';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { ImageMoreInfo } from '../components/image-more-info/image-more-info';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

const MAX_IMAGES_COUNT = 20; // 一页展示多少张图片
const MAX_PAGE_COUNT = 3; // 最多请求多少页

interface DisplayImageInfo {
    src: string;
    width: number;
    height: number;
    sourceUrl: string;
    id: number;
}

interface IReplaceImageDrawerProps {
    selectionService: ISelectionService;
    eventEmitter: EventEmitter;
    defaultKeyword?: string;
}

export const ReplaceImageDrawer = ({
    selectionService,
    eventEmitter,
    defaultKeyword = '',
}: IReplaceImageDrawerProps) => {
    const [selectedElement, setSelectedElement] = useState<SelectedModelType>(); // 当前编辑区选中的元素
    const [displayImagesLeft, setDisplayImagesLeft] = useState<DisplayImageInfo[]>([]);
    const [displayImagesRight, setDisplayImagesRight] = useState<DisplayImageInfo[]>([]);
    const [keyword, setKeyword] = useState<string>(defaultKeyword); // 搜索框显示的内容
    const [checkedId, setCheckedId] = useState<number>();
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [addingImages, setAddingImages] = useState<boolean>(false); // 是否正在进行搜图，兼任防抖的职责
    const [replacingImage, setReplacingImage] = useState<boolean>(false); // 是否正在进行图片替换，图片替换过程中禁用一切操作

    const keywordInput = useRef<HTMLInputElement>(null);
    const galleyDom = useRef<HTMLDivElement>(null);
    const leftGalleyDom = useRef<HTMLDivElement>(null); // 左侧图片列表 dom
    const rightGalleyDom = useRef<HTMLDivElement>(null); // 右侧图片列表元素 dom
    const imagesInfo = useRef<SearchImageInfo[]>([]);
    const displayLeftHeight = useRef<number>(0); // 左侧展示的图片高度
    const displayRightHeight = useRef<number>(0); // 右侧展示的图片高度

    useEffect(() => {
        // 监听选区的变化
        const selectedModelReaction = reaction(
            () => selectionService.getSelectedModel(),
            model => setSelectedElement(model ?? undefined),
            { fireImmediately: true },
        );
        // 监听关键词变化
        const listener = eventEmitter.listen('slide-replace-image-keyword-change', (keyword: string) => {
            setKeyword(keyword);
            addDisplayImages({ keyword, clear: true });
        });
        return () => {
            selectedModelReaction();
            listener.unsubscribe();
        };
    }, []);

    // 侧边栏有空位的时候触发搜图行为，填满侧边栏的空位
    useEffect(() => {
        const maxHeight = galleyDom.current?.clientHeight ?? 0;
        const leftHeight = leftGalleyDom.current?.clientHeight ?? 0;
        const rightHeight = rightGalleyDom.current?.clientHeight ?? 0;
        if (leftHeight < maxHeight && rightHeight < maxHeight) {
            addDisplayImages({ keyword });
        }
    }, [displayImagesLeft, displayImagesRight]);

    // 页面属性变化时，重新展示搜图结果
    useEffect(() => {
        // 图片分为左右两组
        const moreDisplayImages = imagesInfo.current.map((i, index) => ({
            src: i.thumb_url,
            width: i.thumb_width,
            height: i.thumb_height,
            sourceUrl: i.source_url,
            id: index,
        }));
        // TODO: 这里是否可以改为增量
        const imagesLeft: DisplayImageInfo[] = [];
        const imagesRight: DisplayImageInfo[] = [];
        displayLeftHeight.current = 0;
        displayRightHeight.current = 0;
        moreDisplayImages.forEach((image) => {
            if (displayLeftHeight.current <= displayRightHeight.current) {
                displayLeftHeight.current += image.height / image.width;
                imagesLeft.push(image);
            } else {
                displayRightHeight.current += image.height / image.width;
                imagesRight.push(image);
            }
        });
        setDisplayImagesLeft(imagesLeft);
        setDisplayImagesRight(imagesRight);
    }, [addingImages, currentPage]);

    // 可以使用侧边栏替换的图片（多选、单选图片、单选有图片背景的形状）
    const CanSelectedElementReplace = () => {
        if ((selectedElement as IMultiplyModel)?.selectedModels) {
            return true;
        }
        if (selectedElement?.type === UIModelType.IMAGE) {
            return true;
        }
        if (selectedElement?.type === UIModelType.SHAPE) {
            const fillType = selectedElement.getBackgroundFill?.().getFillType();
            if (fillType === FillType.ImageFill) {
                return true;
            }
        }
        return false;
    };

    function onScroll() {
        const { scrollTop = 0, scrollHeight = 0, clientHeight = Number.MAX_SAFE_INTEGER } = galleyDom.current ?? {};
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            addDisplayImages({ keyword });
        }
    }

    // 追加搜图结果
    function addDisplayImages({ keyword = '', clear = false } = {}) {
        const pageIndex = clear ? 0 : currentPage;
        const searchKeyword = keyword || SlideDataServices.SlideTitleManager()?.title;
        if (!searchKeyword || pageIndex >= MAX_PAGE_COUNT || addingImages) {
            return;
        }
        if (clear) {
            imagesInfo.current = [];
            setDisplayImagesLeft([]);
            setDisplayImagesRight([]);
        }
        setAddingImages(true);
        // 此接口使用相同 page 的图片不会重复
        searchImages({ keyword: searchKeyword, num: MAX_IMAGES_COUNT, page: pageIndex }).then((res) => {
            const images = res?.search_image.images ?? [];
            imagesInfo.current = imagesInfo.current.concat(images);
            setAddingImages(false);
            setCurrentPage(pageIndex + 1);
        });
    }

    // 替换正文中的图片
    async function replaceImage() {
        if (checkedId === undefined) {
            return;
        }
        const image = imagesInfo.current[checkedId];
        if (!image) {
            return;
        }
        setReplacingImage(true);
        if (selectedElement?.type === UIModelType.IMAGE || selectedElement?.type === UIModelType.SHAPE) {
            const selectedElements = [selectedElement as IPictureModel];
            ReplaceImageUtils.replaceImagesWithSearchInfo(selectedElements, image, false).then(() =>
                setReplacingImage(false));
            return;
        }
        const { selectedModels } = (selectedElement as IMultiplyModel) ?? {};
        ReplaceImageUtils.replaceImagesWithSearchInfo(selectedModels, image, false).then(() =>
            setReplacingImage(false));
    }

    function showPreview(id?: number) {
        Preview.show({
            disableDownload: true,
            initialIndex: id ?? 0,
            images: imagesInfo.current.map(i => ({ url: i.thumb_url })),
        });
    }

    const DrawerContent = (
        <div
            className={classNames({
                [styles['drawer-content-processing']]: replacingImage,
                [styles['drawer-content']]: true,
            })}
        >
            <Input
                className={styles['drawer-input']}
                value={keyword}
                inputRef={keywordInput}
                showClear={true}
                placeholder={i18n.t('请输入搜索关键词')}
                leftIcon={searchIcon}
                onChange={value => setKeyword(value)}
                onClickLeftIcon={() => {
                    if (keyword) {
                        addDisplayImages({ keyword, clear: true });
                    }
                }}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' && keyword) {
                        addDisplayImages({ keyword, clear: true });
                    }
                }}
            />

            <div
                className={classNames({
                    [styles['drawer-galley']]: true,
                    [styles['galley-loading']]: addingImages && displayImagesLeft.length <= 0,
                })}
                ref={galleyDom}
                onScroll={() => onScroll()}
            >
                <ImagesCol
                    myRef={leftGalleyDom}
                    images={displayImagesLeft}
                    checkedId={checkedId}
                    onImageClick={(id?: number) => setCheckedId(id)}
                    onDoubleClick={() => replaceImage()}
                    onPreViewClick={(id?: number) => showPreview(id)}
                />
                <ImagesCol
                    myRef={rightGalleyDom}
                    images={displayImagesRight}
                    checkedId={checkedId}
                    onImageClick={(id?: number) => setCheckedId(id)}
                    onDoubleClick={() => replaceImage()}
                    onPreViewClick={(id?: number) => showPreview(id)}
                />
            </div>

            <Button
                type="primary"
                className={styles['drawer-confirm']}
                disabled={!CanSelectedElementReplace() || checkedId === undefined}
                onClick={() => replaceImage()}
            >
                {i18n.t('替换选中的图片')}
            </Button>
        </div>
    );

    return (
        <div
            className={classNames({
                [styles['drawer-mask']]: true,
                [styles['drawer-mask-processing']]: replacingImage,
            })}
        >
            {DrawerContent}
        </div>
    );
};

interface IImageColProps {
    myRef: React.RefObject<HTMLDivElement>;
    images: DisplayImageInfo[];
    checkedId?: number;
    onImageClick: (id?: number) => void;
    onDoubleClick: () => void;
    onPreViewClick: (id?: number) => void;
}

const ImagesCol = ({ myRef, images, checkedId, onImageClick, onPreViewClick, onDoubleClick }: IImageColProps) => (
    <div ref={myRef} className={styles['images-col']}>
        {images.map((image, index) => (
            <ImageItem
                key={`${image.src}${index}`}
                image={image}
                checked={checkedId === image.id}
                onClick={onImageClick}
                onDoubleClick={onDoubleClick}
                onPreViewClick={onPreViewClick}
            />
        ))}
    </div>
);

interface IImageItemProps {
    image?: DisplayImageInfo;
    checked?: boolean;
    onClick?: (id?: number) => void;
    onDoubleClick?: () => void;
    onPreViewClick?: (id?: number) => void;
}

const ImageItem = ({ image, checked, onClick, onPreViewClick, onDoubleClick }: IImageItemProps) => (
    <div
        className={classNames({
            [styles['drawer-image']]: true,
            [styles['drawer-image-active']]: checked,
        })}
        onClick={() => onClick?.(image?.id)}
        onDoubleClick={() => onDoubleClick?.()}
        style={{ paddingTop: `calc(100% / ${image?.width} * ${image?.height})` }}
    >
        <img src={image?.src} />
        <ImageMoreInfo
            className={styles['image-more-info']}
            sourceUrl={image?.sourceUrl}
            onPreViewClick={() => onPreViewClick?.(image?.id)}
        />
    </div>
);
